import { CardDetailHeader } from '@src/components/CardDetail/CardDetail';
import { PersonCard } from '@src/components/PersonCard/PersonCard';
import { WardCard } from './WardCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo, FullCardInfo } from '@lub0v/santa-utils';
import {
  Button,
  CardDetail,
  Confetti,
  HiddenFace,
  HintError,
  Note,
  ParticipantFace,
  Stack,
  Typography,
  showDialog,
  showToast,
} from '@src/components';
import './WardView.css';

import { boxExchangePath, boxMessengerPath } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
const CELEBRATION_MS = 5000;

export interface WardViewProps {
  info: BoxInfo;
  card: FullCardInfo | null;
  ward: FullCardInfo | null;
  wardSummary?: CardInfo;
  onChanged: () => Promise<void> | void;
}

export function WardView({ info, card, ward, wardSummary, onChanged }: WardViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const arrivalClass = location.state?.gifteeRevealArrival ? ' ward-detail--arriving' : '';
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [showRevealResult, setShowRevealResult] = useState(false);
  const archived = Boolean(info.box.isArchived);
  const hasGiftee = Boolean(info.wardId);
  const revealed = Boolean(hasGiftee && card?.isWardKnown && ward);
  const sent = Boolean(wardSummary?.isGiftOnItsWay);

  useEffect(() => {
    if (!celebrating) {
      return undefined;
    }
    const stop = window.setTimeout(() => setCelebrating(false), CELEBRATION_MS);
    return () => window.clearTimeout(stop);
  }, [celebrating]);

  async function reveal() {
    if (!card || !hasGiftee || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    const result = await santaClient.saveCard({
      key: info.box.key,
      username: card.username,
      isWardKnown: true,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error?.message ?? 'errors.UNKNOWN');
      return;
    }
    setShowRevealResult(true);
    await onChanged();
    setCelebrating(true);
  }

  async function markSent() {
    setBusy(true);
    const res = await santaClient.writeToWard({
      key: info.box.key,
      message: t('game.gift.sentMessage'),
      isGiftSent: true,
      isAutomatic: true,
    });
    setBusy(false);
    if (!res.ok) {
      showToast(t(res.error?.message || 'newBox.saveFailed'));
      return;
    }
    showToast(t('game.gift.sentDone'), 'success');
    await onChanged();
  }

  async function undoSent() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await santaClient.getWardMessages({ key: info.box.key });
      if (!result.ok) throw new Error(result.error?.message || 'newBox.saveFailed');
      const notices = (result.data || []).filter(
        (message) => message.isYou && message.isGiftSent && message.active !== false,
      );
      for (const message of notices) {
        const deleted = await santaClient.updateOwnMessage({
          key: info.box.key,
          id: message.id,
          message: '',
        });
        if (!deleted.ok) throw new Error(deleted.error?.message || 'newBox.saveFailed');
      }
    } catch (error) {
      showToast(t(error instanceof Error ? error.message : 'newBox.saveFailed'));
    } finally {
      setBusy(false);
      await onChanged();
    }
  }

  function confirmSent() {
    showDialog({
      title: t('game.gift.sentSubmit'),
      children: t('game.gift.sentMessage'),
      cancel: t('box.cancel'),
      submit: t('game.gift.sentSubmit'),
      onSubmit: markSent,
    });
  }

  if (!info.isActiveDraw || !hasGiftee) {
    return (
      <Stack gap={6} testId="game-ward">
        <CardDetailHeader
          title={t('game.exchange.giftee')}
          backLabel={t('game.exchange.title')}
          onBack={() => navigate(boxExchangePath(info.box.key))}
          testId="game-ward-back"
        />
        <Note tone="info">{t(!info.isActiveDraw ? 'game.ward.waiting' : info.isAdmin ? 'game.exchange.noGifteeOrganizer' : 'game.exchange.noGifteeParticipant')}</Note>
      </Stack>
    );
  }

  if (!revealed || showRevealResult) {
    return (
      <CardDetail testId="game-ward" className={`ward-detail${arrivalClass}`}>
        <CardDetailHeader
          title={t('game.exchange.giftee')}
          backLabel={t('game.exchange.title')}
          onBack={() => navigate(boxExchangePath(info.box.key))}
          testId="game-ward-back"
        />
        <PersonCard
          layout="exchange"
          compact
          edges={2}
          decoration="sparkles"
          testId="game-ward-reveal-card"
          avatar={
            revealed ? (
              <ParticipantFace card={ward} size="round" />
            ) : (
              <HiddenFace
                size="round"
                label={t('box.card.hiddenLabel')}
                testId="game-ward-hidden"
              />
            )
          }
          name={revealed ? ward?.username : t('game.exchange.gifteeHidden')}
          status={
            <Typography as="p" role="body/sm" color="inherit">
              {t(revealed ? 'game.ward.revealedHint' : 'box.card.hint')}
            </Typography>
          }
          actions={
            <Stack gap={2}>
              <HintError error={error} />
              <Button
                variant="primary"
                size="large"
                loading={busy}
                disabled={!revealed && (archived || showRevealResult)}
                onClick={() => (revealed ? setShowRevealResult(false) : void reveal())}
                testId={revealed ? 'game-ward-open-card' : 'game-ward-reveal'}
              >
                {t(revealed ? 'game.ward.openCard' : 'box.card.reveal')}
              </Button>
            </Stack>
          }
        />
        <Confetti active={celebrating} />
      </CardDetail>
    );
  }

  return (
    <CardDetail testId="game-ward" className={`ward-detail${arrivalClass}`}>
      <CardDetailHeader
        title={t('game.exchange.giftee')}
        backLabel={t('game.exchange.title')}
        onBack={() => navigate(boxExchangePath(info.box.key))}
        testId="game-ward-back"
      />
      <WardCard
        info={info}
        ward={ward}
        sent={sent}
        busy={busy}
        onSent={confirmSent}
        onUndoSent={() => void undoSent()}
        onChat={() => navigate(boxMessengerPath(info.box.key, 'giftee'))}
      />
      <Confetti active={celebrating} />
    </CardDetail>
  );
}
